import React, { ReactNode, useCallback } from "react";
import styled, { css } from "styled-components";
import {
  Close,
  Content,
  Description as DialogA11yDescription,
  DialogCloseProps,
  DialogContentProps,
  DialogDescriptionProps,
  DialogOverlayProps,
  DialogPortalProps,
  DialogProps,
  DialogTitleProps,
  Overlay,
  Portal,
  Root,
  Title as DialogA11yTitle,
  Trigger,
} from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  Button,
  Icon,
  IconName,
  Image,
  Theme,
  colors,
  font,
  media,
  typography,
} from "@literati/public-ui-shared-react";
import {
  AuthorSchema,
  ContributorSchema,
  SnakeToCamelCaseNested,
} from "@literati/kids-api";
import { BookDetails } from "lib/api/account";
import { trimTags } from "utils/string";
import BookShuffleExpandableText from "components/bookShuffle/BookShuffleExpandableText";
import { translateUp } from "utils/animations/keyframes";
import {
  dialogOverlayStyles,
  fadeInOverlayRgba,
} from "components/bookShuffle/bookShuffleStyles";
import { formatAuthorNames } from "components/bookShuffle/utils";
import analytics from "analytics";

const COVER_IMG_PLACEHOLDER =
  "https://ctfassets.literati.com/vqtz4cq2w5ts/28qbG2kRvefzvWF5y5rUCN/fead1dfa0c8f2a0d4c2699bbe8a6cc23/Universal.png";

/** This is the className that will be pre-pended to all of BookShuffleDialogBookDetails's inner component classNames */
export const BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE =
  "book-shuffle-dialog-book-details";

/** These are all of the internal classNames that are used within the BookShuffleDialogBookDetails component */
export const BookShuffleDialogBookDetailsClassName = {
  BASE: BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE,
  CONTENT: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-content`,
  CONTENT_INNER_CONTAINER: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-content-inner-container`,
  HEADER_SECTION: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-header-section`,
  CLOSE_BUTTON_ROW: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-close-button-row`,
  CLOSE: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-close`,
  HEADER_CONTENT_CONTAINER: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-header-content-container`,
  HEADER_CONTENT_INNER_CONTAINER: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-header-content-inner-container`,
  BOOK_TITLE_AUTHOR_ROW: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-book-title-author-row`,
  BOOK_TITLE: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-book-title`,
  BOOK_AUTHOR: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-book-author`,
  DETAILS_SECTION: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-details-section`,
  DETAILS_SECTION_INNER_CONTAINER: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-details-section-inner-container`,
  DETAILS_SUBSECTION: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-details-subsection`,
  SUMMARY_CARD: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-summary-card`,
  AUTHOR_DETAILS_SUBSECTION: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-author-details-subsection`,
  AUTHOR_CARD: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-author-card`,
  AUTHOR_EYEBROW: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-author-eyebrow`,
  AUTHOR_NAME: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-author-name`,
  AUTHOR_BIO: `${BOOK_SHUFFLE_DIALOG_BOOK_DETAILS_CLASS_BASE}-author-bio`,
} as const;

export function shouldShowDetailsSectionElements(
  contributors?: BookShuffleContributor[],
  description?: string
) {
  const showAuthorDetailsSubsection = Boolean(
    contributors?.find((contributor) => Boolean(contributor.bio))
  );
  const showDescription = Boolean(description);
  const showDetailsSection = showDescription || showAuthorDetailsSubsection;

  return {
    showAuthorDetailsSubsection,
    showDescription,
    showDetailsSection,
  };
}

const StyledOverlay = styled(Overlay)`
  ${dialogOverlayStyles}

  &[data-state='open'] {
    animation: ${fadeInOverlayRgba} 500ms;
  }

  .${BookShuffleDialogBookDetailsClassName.CONTENT}[data-state='open'] {
    animation: ${translateUp} 500ms ease-out;
  }
`;

const StyledContent = styled(Content)`
  position: relative;
  margin: 5.5rem 0 0;
  height: calc(100% - 5.5rem);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  ${media.xl} {
    align-items: center;
    width: 1440px;
  }
`;

const ContentInnerContainer = styled.div<{ $showDetailsSection: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 376px;
  height: 100%;
  overflow: scroll;
  border-radius: ${({ $showDetailsSection }) =>
    $showDetailsSection ? "1rem 1rem 0 0" : "1rem"};
  background: ${colors.white};

  ${media.xl} {
    min-width: 1440px;
  }
`;

const ScrollableContainer = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  overflow: scroll;
`;

const HeaderSection = styled.section`
  padding: 4rem 1.5rem 3rem;
`;

const CloseButtonRow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding: 1.5rem 1.5rem 2rem;
  background-image: linear-gradient(white, transparent);
  border-radius: 1rem 1rem 0 0;
  z-index: 2;

  ${media.lg} {
    padding: 1.5rem 1.5rem 0;
  }
`;

const CloseButton = styled(Button)`
  min-height: 1.5rem;
  width: 1.5rem;
`;

const HeaderContentContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const HeaderContentInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;

  ${media.lg} {
    flex-direction: row;
    justify-content: flex-start;
    gap: 4rem;
    width: 748px;
  }
`;

const BookImageContainer = styled.div<{ $hasImage: boolean }>`
  position: relative;
  flex-shrink: 0;

  ${({ $hasImage }) =>
    $hasImage
      ? `
    height: 165px;
    width: 128px;

    ${media.md} {
      height: 255px;
      width: 183px;
    }

    ${media.lg} {
      height: 346px;
      width: 248px;
    }
  `
      : `
    height: 128px;
    width: 128px;
  `}
`;

const BookTitleAuthorRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  ${media.lg} {
    text-align: left;
    align-items: flex-start;
    justify-content: center;
  }
`;

const BookTitle = styled.h1`
  font-family: ${font.fontFamily.hoeflerTitling};
  font-size: 2rem;
  line-height: 2.6rem;
  color: ${colors.inkBlack};
`;

const BookAuthor = styled.p`
  margin: 0.5rem 0 0;
  color: ${colors.warmGray3};
`;

const DetailsSection = styled.section`
  flex: 2;
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 0 0 4.5rem;
  background: ${colors.papierBlanc};
`;

const DetailsSectionInnerContainer = styled.div`
  max-width: 748px;
`;

const DetailsSubsection = styled.div`
  padding: 3rem 1.5rem 0;

  ${media.lg} {
    padding: 3rem 0 0;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${colors.inkBlack};
`;

const cardStyles = css`
  margin: 1rem 0 0;
  border-radius: 12px;
  background: ${colors.white};
`;

const SummaryCard = styled.div`
  ${cardStyles}
  padding: 1.25rem;
`;

const AuthorCard = styled.div`
  ${cardStyles}
  padding: 1.75rem 1.25rem 1.5rem;
`;

const AuthorEyebrow = styled.h3`
  ${typography.eyebrow}
`;

const AuthorName = styled.p`
  margin: 0.25rem 0 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: ${colors.inkBlack};
`;

const AuthorBio = styled.p`
  margin: 1rem 0 0;
`;

export type BookShuffleDialogBookDetailsOptions = {
  closeProps?: DialogCloseProps;
  contentProps?: DialogContentProps;
  descriptionProps?: DialogDescriptionProps;
  portalProps?: DialogPortalProps;
  overlayProps?: DialogOverlayProps;
  rootProps?: DialogProps;
  titleProps?: DialogTitleProps;
};

export type BookShuffleAuthor = Omit<
  SnakeToCamelCaseNested<AuthorSchema>,
  "photo"
>;
export type BookShuffleContributor = Omit<
  SnakeToCamelCaseNested<ContributorSchema>,
  "photo"
>;
export type BookShuffleBookDetails = Pick<
  BookDetails,
  "description" | "externalCoverUrl" | "title"
>;

export interface BookShuffleDialogBookDetailsProps
  extends BookShuffleBookDetails {
  className?: string;
  contributors?: BookShuffleContributor[];
  dialogA11yDescription?: string;
  dialogA11yTitle?: ReactNode;
  dialogOptions?: BookShuffleDialogBookDetailsOptions;
  trigger?: ReactNode;
}

export const BookShuffleDialogBookDetails = ({
  className = "",
  contributors,
  description,
  dialogA11yDescription,
  dialogA11yTitle,
  dialogOptions = {},
  externalCoverUrl,
  title,
  trigger,
}: BookShuffleDialogBookDetailsProps) => {
  const cls = BookShuffleDialogBookDetailsClassName;

  const getContributorNames = useCallback(
    () => formatAuthorNames(contributors),
    [contributors]
  );
  const contributorNames = getContributorNames();

  const a11yTitle = dialogA11yTitle || `"${title}" by ${contributorNames}`;
  const a11yDescription =
    dialogA11yDescription || `Book details about the book "${title}"`;

  const imgSrc = externalCoverUrl || COVER_IMG_PLACEHOLDER;
  const hasImage = Boolean(externalCoverUrl);
  const sizes = hasImage
    ? "(min-width: 768px) 184px, (min-width: 1024px) 268px, 128px"
    : "128px";

  const { showAuthorDetailsSubsection, showDescription, showDetailsSection } =
    shouldShowDetailsSectionElements(contributors, description);

  return (
    <Theme>
      <Root {...dialogOptions?.rootProps}>
        <Trigger asChild>{trigger}</Trigger>
        <Portal {...dialogOptions?.portalProps}>
          <StyledOverlay
            {...dialogOptions?.overlayProps}
            className={`${cls.BASE} ${className}`}
          >
            <StyledContent
              {...dialogOptions?.contentProps}
              className={cls.CONTENT}
            >
              <ContentInnerContainer
                $showDetailsSection={showDetailsSection}
                className={cls.CONTENT_INNER_CONTAINER}
              >
                <CloseButtonRow className={cls.CLOSE_BUTTON_ROW}>
                  <Close
                    asChild
                    {...dialogOptions?.closeProps}
                    className={cls.CLOSE}
                  >
                    <CloseButton
                      aria-label="Close"
                      variant="unstyled"
                      onClick={() =>
                        analytics.trackAmplitudeClickEvent(
                          "Book Details Dialog Close Button"
                        )
                      }
                    >
                      <Icon name={IconName.X} />
                    </CloseButton>
                  </Close>
                </CloseButtonRow>
                <ScrollableContainer>
                  <HeaderSection className={cls.HEADER_SECTION}>
                    <VisuallyHidden>
                      <DialogA11yTitle {...dialogOptions?.titleProps}>
                        {a11yTitle}
                      </DialogA11yTitle>
                    </VisuallyHidden>
                    <VisuallyHidden>
                      <DialogA11yDescription
                        {...dialogOptions?.descriptionProps}
                      >
                        {a11yDescription}
                      </DialogA11yDescription>
                    </VisuallyHidden>
                    <HeaderContentContainer
                      className={cls.HEADER_CONTENT_CONTAINER}
                    >
                      <HeaderContentInnerContainer
                        className={cls.HEADER_CONTENT_INNER_CONTAINER}
                      >
                        <BookImageContainer $hasImage={hasImage}>
                          <Image
                            src={imgSrc}
                            alt={title ?? ""}
                            layout="fill"
                            objectFit="contain"
                            sizes={sizes}
                          />
                        </BookImageContainer>
                        <BookTitleAuthorRow
                          className={cls.BOOK_TITLE_AUTHOR_ROW}
                        >
                          <BookTitle className={cls.BOOK_TITLE}>
                            {title}
                          </BookTitle>
                          {contributorNames && (
                            <BookAuthor className={cls.BOOK_AUTHOR}>
                              by {contributorNames}
                            </BookAuthor>
                          )}
                        </BookTitleAuthorRow>
                      </HeaderContentInnerContainer>
                    </HeaderContentContainer>
                  </HeaderSection>
                  {showDetailsSection && (
                    <DetailsSection className={cls.DETAILS_SECTION}>
                      <DetailsSectionInnerContainer
                        className={cls.DETAILS_SECTION_INNER_CONTAINER}
                      >
                        {showDescription && (
                          <DetailsSubsection className={cls.DETAILS_SUBSECTION}>
                            <SectionHeading>Summary</SectionHeading>
                            <SummaryCard className={cls.SUMMARY_CARD}>
                              <BookShuffleExpandableText lineClamp={3}>
                                <span
                                  // In practice, this should only contain html entities
                                  dangerouslySetInnerHTML={{
                                    __html: description,
                                  }}
                                />
                              </BookShuffleExpandableText>
                            </SummaryCard>
                          </DetailsSubsection>
                        )}
                        {showAuthorDetailsSubsection && (
                          <DetailsSubsection
                            className={cls.AUTHOR_DETAILS_SUBSECTION}
                          >
                            <SectionHeading>Behind the Book</SectionHeading>
                            {contributors?.map((contributor, idx) => {
                              const { isAuthor, isIllustrator } = contributor;

                              if (!isAuthor && !isIllustrator) {
                                return null;
                              }

                              const eyebrow = isAuthor
                                ? "AUTHOR"
                                : isIllustrator
                                ? "ILLUSTRATOR"
                                : null;

                              return (
                                <AuthorCard
                                  className={cls.AUTHOR_CARD}
                                  key={`contributor-${idx}`}
                                >
                                  {eyebrow && (
                                    <AuthorEyebrow
                                      className={cls.AUTHOR_EYEBROW}
                                    >
                                      {eyebrow}
                                    </AuthorEyebrow>
                                  )}
                                  <AuthorName className={cls.AUTHOR_NAME}>
                                    {contributor.name}
                                  </AuthorName>
                                  <AuthorBio className={cls.AUTHOR_BIO}>
                                    {trimTags(contributor.bio)}
                                  </AuthorBio>
                                </AuthorCard>
                              );
                            })}
                          </DetailsSubsection>
                        )}
                      </DetailsSectionInnerContainer>
                    </DetailsSection>
                  )}
                </ScrollableContainer>
              </ContentInnerContainer>
            </StyledContent>
          </StyledOverlay>
        </Portal>
      </Root>
    </Theme>
  );
};

export default BookShuffleDialogBookDetails;
